import type {
  ReploAnnouncement,
  ReploAnnouncementTag,
} from "schemas/generated/announcement";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { extractTextFromFirstParagraph } from "@v2/features/announcements/utils/announcement";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { ArrowLeft, ArrowUpRight, X } from "lucide-react";

const AnnouncementCard: React.FC<{
  announcement: ReploAnnouncement;
  onAnnouncementClick: (announcement: ReploAnnouncement) => void;
}> = ({ announcement, onAnnouncementClick }) => {
  return (
    <div
      className="hover:bg-neutral-soft cursor-pointer rounded-sm p-2"
      onClick={() => onAnnouncementClick(announcement)}
    >
      <div className="flex gap-2">
        {announcement.imageUrl && (
          <div className="bg-neutral-soft h-[52px] w-[52px] shrink-0 rounded-sm">
            <img
              src={announcement.imageUrl}
              alt=""
              className="h-full w-full rounded-sm object-cover"
              loading="lazy"
            />
          </div>
        )}
        <div className="flex flex-col gap-1 overflow-hidden">
          <h3 className="text-xs font-semibold overflow-hidden truncate">
            {announcement.title}
          </h3>
          <p className="text-muted line-clamp-2 text-xs">
            {extractTextFromFirstParagraph(announcement.description)}
          </p>
        </div>
      </div>
    </div>
  );
};

const AnnouncementCardSkeleton: React.FC = () => {
  return (
    <div className="rounded-sm p-2">
      <div className="flex gap-2">
        <div className="h-[52px] w-[52px] shrink-0 rounded-sm">
          <Skeleton className="h-full w-full rounded-sm" />
        </div>
        <div className="flex flex-grow flex-col gap-1 overflow-hidden">
          <Skeleton className="h-5 w-3/4 rounded-sm" />
          <Skeleton className="h-4 w-full rounded-sm" />
          <Skeleton className="h-4 w-1/2 rounded-sm" />
        </div>
      </div>
    </div>
  );
};

const AnnouncementTag: React.FC<{ tag: ReploAnnouncementTag }> = ({ tag }) => {
  const getVariant = () => {
    switch (tag) {
      case "New":
        return "info";
      case "Update":
        return "success";
      case "Insights":
      case "A/B Testing":
        return "beta";
      default:
        return "neutral";
    }
  };

  return (
    <Tag size="base" variant={getVariant()}>
      {tag}
    </Tag>
  );
};

const FullAnnouncementView: React.FC<{
  announcement: ReploAnnouncement;
  onBack: () => void;
  onClose: () => void;
}> = ({ announcement, onBack, onClose }) => {
  const logEvent = useLogAnalytics();
  const handleAnnouncementClick = () => {
    if (announcement && announcement.ctaLink) {
      window.open(announcement.ctaLink, "_blank");
      logEvent("announcements.clicked", {
        announcementId: announcement.id,
        announcementWebflowId: announcement.webflowId,
        source: "AnnouncementsMenu",
      });
    }
  };

  return (
    <div className="flex h-[375px] flex-col">
      <div className="border-neutral-soft shadow-sm sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-white px-4 py-3">
        <Button icon onClick={onBack} variant="tertiary" size="sm">
          <ArrowLeft />
        </Button>

        <h3 className="header-h6 overflow-hidden truncate">
          {announcement.title}
        </h3>

        <Button icon onClick={onClose} variant="tertiary" size="sm">
          <X />
        </Button>
      </div>

      <div className="no-scrollbar flex h-full flex-col gap-4 overflow-y-auto overflow-x-hidden p-4">
        {announcement.imageUrl && (
          <div className="bg-neutral-soft w-full rounded-sm">
            <img
              src={announcement.imageUrl}
              alt="Announcement image"
              className="h-full w-full rounded-sm object-cover"
              loading="lazy"
            />
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <h2 className="header-h6">{announcement.title}</h2>
            <div className="flex gap-2">
              {announcement.tags.map((tag) => (
                <AnnouncementTag key={tag} tag={tag} />
              ))}
            </div>
          </div>

          <div
            className="[&_a]:text-primary break-words text-sm"
            dangerouslySetInnerHTML={{ __html: announcement.description }}
          />

          {announcement.ctaText && announcement.ctaLink && (
            <div className="flex">
              <Button
                variant="primary"
                size="sm"
                onClick={handleAnnouncementClick}
              >
                {announcement.ctaText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AnnouncementsMenuContent: React.FC<{
  announcements: ReploAnnouncement[];
  isLoading: boolean;
  handleClose: () => void;
  selectedAnnouncement: ReploAnnouncement | null;
  setSelectedAnnouncement: (announcement: ReploAnnouncement | null) => void;
}> = ({
  announcements,
  isLoading,
  handleClose,
  selectedAnnouncement,
  setSelectedAnnouncement,
}) => {
  if (selectedAnnouncement) {
    return (
      <FullAnnouncementView
        announcement={selectedAnnouncement}
        onBack={() => setSelectedAnnouncement(null)}
        onClose={handleClose}
      />
    );
  }

  return (
    <div className="flex h-full flex-col gap-1">
      <div className="flex flex-col gap-2">
        {isLoading ? (
          <>
            {new Array(3).fill(0).map((_, index) => (
              <AnnouncementCardSkeleton key={index} />
            ))}
          </>
        ) : (
          announcements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              onAnnouncementClick={() => setSelectedAnnouncement(announcement)}
            />
          ))
        )}
      </div>

      <div className="flex justify-end">
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            window.open("https://www.replo.app/changelog", "_blank");
          }}
          endEnhancer={<ArrowUpRight />}
          UNSAFE_className="focus:outline-hidden"
        >
          See all
        </Button>
      </div>
    </div>
  );
};

export default AnnouncementsMenuContent;
